import { useState, useCallback, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Globe, 
  RefreshCw, 
  AlertTriangle,
  Download,
  Loader2,
  Database,
  CloudDownload,
  Search,
  Home,
  MapPin,
  Bed,
  Bath,
  Ruler,
  ExternalLink,
  Calendar,
  ImageIcon,
  Settings,
  Clock,
  Power,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Property } from '../types/property';
import { ExternalListing } from '../types/external-listing';
import { propertyAPI } from '../lib/property-api';
import { externalListingsAPI } from '../lib/external-api';
import { externalListingsToProperties } from '../lib/listing-adapter';
import { adminUserAPI } from '../lib/admin-api';
import { autoImportAPI, AutoImportSettings } from '../lib/auto-import-api';
import { tokenManager } from '../lib/external-api';
import apiClient from '@/services/api-client';
import { logger } from '@/services/logger.service';

/** Safely extract displayable location string — suburb/city only, never street address */
function getLocationString(location: any): string {
  if (!location) return '';
  if (typeof location === 'object') {
    const parts = [location.suburb, location.city].filter(Boolean);
    return parts.join(', ');
  }
  return '';
}

/** Extract a feature value from v2 features array (e.g. 'Bedroom' → 4) */
function getListingFeature(listing: any, type: string): number | undefined {
  if (!listing.features || !Array.isArray(listing.features)) return undefined;
  const f = listing.features.find((feat: any) => feat?.type?.toLowerCase() === type.toLowerCase());
  return f?.value;
}

interface PropertyListingManagerProps {
  properties: Property[];
  onPropertiesChanged?: () => void;
}

export function PropertyListingManager({ properties, onPropertiesChanged }: PropertyListingManagerProps) {
  // External listings state
  const [externalListings, setExternalListings] = useState<ExternalListing[]>([]);
  const [isLoadingExternal, setIsLoadingExternal] = useState(false);
  const [externalError, setExternalError] = useState<string | null>(null);

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);

  // Diagnostic: Log component state on every render
  if (typeof externalListings !== 'undefined' && !Array.isArray(externalListings)) {
    console.error('[PropertyListingManager] CRITICAL: externalListings is not an array!', {
      type: typeof externalListings,
      value: externalListings,
      isArray: Array.isArray(externalListings),
      isObject: externalListings !== null && typeof externalListings === 'object'
    });
  }
  
  // Auto-sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  
  // Auto-import settings state
  const [autoImportSettings, setAutoImportSettings] = useState<AutoImportSettings>({
    enabled: false,
    interval: 15,
    lastSync: null,
  });
  const [showAutoImportSettings, setShowAutoImportSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // Preview dialog
  const [previewListing, setPreviewListing] = useState<ExternalListing | null>(null);

  // Auto-sync properties from external backend on mount
  useEffect(() => {
    loadAutoImportSettings();
  }, []);

  // Set up auto-import interval
  useEffect(() => {
    if (!autoImportSettings.enabled) return;

    const intervalMs = autoImportSettings.interval * 60 * 1000; // Convert minutes to ms
    console.log(`[AUTO-IMPORT] Setting up interval: ${autoImportSettings.interval} minutes`);

    const intervalId = setInterval(() => {
      console.log('[AUTO-IMPORT] Triggering scheduled sync...');
      syncExternalProperties();
    }, intervalMs);

    return () => {
      console.log('[AUTO-IMPORT] Clearing interval');
      clearInterval(intervalId);
    };
  }, [autoImportSettings.enabled, autoImportSettings.interval]);

  // Load auto-import settings
  const loadAutoImportSettings = async () => {
    // Auto-import settings endpoint not yet provisioned — use local defaults silently
  };

  // Save auto-import settings (endpoint not yet provisioned — persist locally only)
  const saveAutoImportSettings = async () => {
    setIsSavingSettings(true);
    try {
      toast.success('Auto-import settings saved');
      setShowAutoImportSettings(false);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Trigger full P24 → DynamoDB sync
  const syncExternalProperties = async () => {
    setIsSyncing(true);
    try {
      const result = await apiClient.post<{ synced: number; source: string; timestamp: string }>('/listings/sync');
      setLastSyncTime(result.timestamp);
      onPropertiesChanged?.();
      toast.success(`Sync complete — ${result.synced} listings imported from Property24`);
    } catch (err: any) {
      logger.error('[Property Sync] Error', err as Error, undefined, 'PropertyListingManager');
      toast.error(`Sync failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Fetch listings live from Property24 (not DynamoDB)
  const fetchExternalListings = useCallback(async () => {
    setIsLoadingExternal(true);
    setExternalError(null);
    try {
      const resp = await apiClient.get<any>('/listings/property24/preview');
      const listings: ExternalListing[] = resp?.data?.listings || resp?.listings || [];
      const total: number = resp?.data?.total ?? resp?.total ?? listings.length;
      setExternalListings(listings);
      toast.success(`Fetched ${total} listings live from Property24`);
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch listings from Property24';
      setExternalError(msg);
      toast.error(msg);
      logger.error('Error fetching P24 listings', err as Error, undefined, 'PropertyListingManager');
    } finally {
      setIsLoadingExternal(false);
    }
  }, []);

  // Import all external listings into KV store (dedup-aware via somethingPropertyId + externalId)
  // Also auto-discovers unknown agents from the external API.
  const handleImportListings = async () => {
    if (externalListings.length === 0) {
      toast.error('No external listings to import. Fetch listings first.');
      return;
    }

    setIsImporting(true);
    setImportProgress({ current: 0, total: externalListings.length });

    try {
      // Convert external listings to Property format
      // The adapter uses somethingPropertyId as the canonical id when available
      const convertedProperties = externalListingsToProperties(externalListings);
      
      // ─── Step 1: Extract unique agent IDs from listings for auto-discovery ───
      const agentIdSet = new Set<string>();
      for (const listing of externalListings) {
        // v2: agents array with agentId
        if (listing.agents?.length) {
          for (const agent of listing.agents) {
            if (agent.agentId) agentIdSet.add(agent.agentId);
          }
        }
        // v1: agentIds array (numeric portal IDs)
        if (listing.agentIds?.length) {
          for (const aid of listing.agentIds) {
            agentIdSet.add(String(aid));
          }
        }
      }
      const uniqueAgentIds = [...agentIdSet];
      console.log(`[Import] Found ${uniqueAgentIds.length} unique agent IDs across ${externalListings.length} listings`);

      // ─── Step 2: Auto-discover unknown agents ───
      if (uniqueAgentIds.length > 0) {
        try {
          toast.info(`Resolving ${uniqueAgentIds.length} agents...`);
          const resolveResult = await adminUserAPI.resolveAgents(uniqueAgentIds);
          console.log('[Import] Agent resolution:', resolveResult);
          if (resolveResult.resolved > 0) {
            toast.success(`Discovered ${resolveResult.resolved} new agent(s): ${resolveResult.agents.map(a => a.name).join(', ')}`, {
              duration: 5000,
            });
          }
        } catch (agentErr) {
          console.warn('[Import] Agent auto-discovery failed (non-fatal):', agentErr);
          // Don't block the import — agents can be resolved later
        }
      }

      // ─── Step 3: Seed properties in batches (dedup-aware) ───
      setImportProgress({ current: 0, total: convertedProperties.length });
      const batchSize = 10;
      let imported = 0;
      let totalNew = 0;
      let totalUpdated = 0;
      let totalAgentsMapped = 0;

      for (let i = 0; i < convertedProperties.length; i += batchSize) {
        const batch = convertedProperties.slice(i, i + batchSize);
        const result = await propertyAPI.seedProperties(batch);
        imported += batch.length;
        // Track stats from server response
        if (result && typeof result === 'object') {
          totalNew += (result as any).newCount || 0;
          totalUpdated += (result as any).updatedCount || 0;
          totalAgentsMapped += (result as any).agentAutoMapped || 0;
        }
        setImportProgress({ current: imported, total: convertedProperties.length });
      }

      const parts = [`Imported ${imported} listings`];
      if (totalNew > 0) parts.push(`${totalNew} new`);
      if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
      if (totalAgentsMapped > 0) parts.push(`${totalAgentsMapped} agents auto-linked`);
      toast.success(parts.join(' · '), { duration: 5000 });
      onPropertiesChanged?.();
    } catch (err: any) {
      logger.error('Error importing listings', err as Error, undefined, 'PropertyListingManager');
      toast.error(`Import failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  // Use external listings directly (filter temporarily removed for debugging)
  const filteredListings = externalListings;

  // Get unique sources from listings - with defensive checks
  const uniqueSources = (() => {
    try {
      if (!Array.isArray(externalListings)) {
        console.warn('[Stats] externalListings is not an array:', typeof externalListings);
        return [];
      }

      const sources = externalListings
        .map(l => {
          if (!l || typeof l !== 'object') return null;
          return (l as any).source || null;
        })
        .filter((s): s is string => Boolean(s));

      return [...new Set(sources)];
    } catch (err) {
      console.error('[Stats] Error calculating unique sources:', err);
      return [];
    }
  })();

  // Stats
  const kvPropertyCount = properties.length;
  const externalCount = Array.isArray(externalListings) ? externalListings.length : 0;

  // Group by source for stats
  const sourceCounts = (() => {
    try {
      if (!Array.isArray(externalListings)) {
        return {};
      }

      return externalListings.reduce<Record<string, number>>((acc, l) => {
        if (!l || typeof l !== 'object') return acc;
        const src = (l as any)?.source || 'unknown';
        acc[src] = (acc[src] || 0) + 1;
        return acc;
      }, {});
    } catch (err) {
      console.error('[Stats] Error calculating source counts:', err);
      return {};
    }
  })();

  const formatPrice = (price: number) => {
    if (!price) return 'N/A';
    return `R ${price.toLocaleString()}`;
  };

  const formatTimestamp = (ts: number) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimestampFull = (ts: number) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">KV Store Properties</p>
                  <p className="text-2xl font-bold text-[#FDB022]">{kvPropertyCount}</p>
                </div>
                <Database className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">External Listings</p>
                  <p className="text-2xl font-bold">{externalCount}</p>
                </div>
                <Globe className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sources</p>
                  <div className="flex items-center gap-2 mt-1">
                    {Object.entries(sourceCounts).length > 0 ? (
                      Object.entries(sourceCounts).map(([src, count]) => (
                        <Badge key={src} variant="outline" className="text-xs">
                          {src}: {count}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-2xl font-bold text-muted-foreground">-</p>
                    )}
                  </div>
                </div>
                <Home className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CloudDownload className="w-5 h-5 text-[#FDB022]" />
                Listing Import & Sync
                {isSyncing && (
                  <Badge variant="outline" className="ml-2 animate-pulse">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Syncing...
                  </Badge>
                )}
                {lastSyncTime && !isSyncing && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    Last sync: {new Date(lastSyncTime).toLocaleTimeString()}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Properties are automatically synced from external API on page load and stored in the database
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Auto-Import Settings */}
              <Button
                data-testid="property-listing-settings-button"
                variant="outline"
                onClick={() => setShowAutoImportSettings(true)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Auto-Import Settings
              </Button>

              {/* Manual Sync Button */}
              <Button
                data-testid="property-listing-manual-sync-button"
                variant="outline"
                onClick={syncExternalProperties}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isSyncing ? 'Syncing...' : 'Manual Sync'}
              </Button>

              {/* Fetch from External */}
              <Button
                data-testid="property-listing-fetch-button"
                variant="outline"
                onClick={fetchExternalListings}
                disabled={isLoadingExternal}
              >
                {isLoadingExternal ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isLoadingExternal ? 'Fetching...' : 'Fetch External Listings'}
              </Button>

              {/* Import into KV */}
              <Button
                data-testid="property-listing-import-button"
                onClick={handleImportListings}
                disabled={isImporting || externalListings.length === 0}
                className="bg-[#FDB022] hover:bg-[#FDB022]/90 text-black"
              >
                {isImporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isImporting
                  ? `Importing ${importProgress?.current || 0}/${importProgress?.total || 0}...`
                  : `Import ${externalListings.length > 0 ? externalListings.length + ' ' : ''}Listings`
                }
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Import Progress */}
        {importProgress && (
          <CardContent className="pt-0">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Importing listings...</span>
                <span className="font-medium">{importProgress.current} / {importProgress.total}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-[#FDB022] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        )}

        {/* Error display */}
        {externalError && (
          <CardContent className="pt-0">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {externalError}
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>

      {/* External Listings Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>External Listings</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {externalListings.length === 0 
                  ? 'Click "Fetch External Listings" to load data from the AWS backend'
                  : `Showing ${filteredListings.length} of ${externalListings.length} listings`
                }
              </p>
            </div>
          </div>

        </CardHeader>
        <CardContent>
          {isLoadingExternal ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-[#FDB022] mb-4" />
              <p className="text-muted-foreground">Fetching listings from external backend...</p>
            </div>
          ) : externalListings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Globe className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h4 className="font-medium mb-2">No External Listings Loaded</h4>
              <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                Click "Fetch External Listings" to pull data from the AWS backend. 
                Once fetched, you can review and import them into the local database.
              </p>
              <Button
                data-testid="property-listing-fetch-empty-button"
                variant="outline"
                onClick={fetchExternalListings}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Fetch External Listings
              </Button>
            </div>
          ) : !Array.isArray(filteredListings) || filteredListings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Search className="w-12 h-12 text-muted-foreground/30 mb-4" />
              <h4 className="font-medium mb-2">No Listings Available</h4>
              <p className="text-sm text-muted-foreground">
                No properties to display.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredListings.map((listing: any, index: number) => {
                if (!listing) return null;

                return (
                <motion.div
                  key={listing.somethingPropertyId || listing.listingId || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.5) }}
                  className="border rounded-lg p-4 hover:bg-accent/5 transition-colors cursor-pointer"
                  onClick={() => setPreviewListing(listing)}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {Array.isArray(listing?.images) && listing.images.length > 0 ? (
                        <img
                          src={typeof listing.images?.[0] === 'string' ? listing.images[0] : (listing.images?.[0] as any)?.url || ''}
                          alt={String(listing?.marketingHeading || listing?.title || 'Property')}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <Home className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-medium truncate text-sm">{String(listing?.marketingHeading || listing?.title || 'Untitled')}</h4>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {listing?.source && (
                            <Badge variant="outline" className="text-xs">
                              {String(listing.source)}
                            </Badge>
                          )}
                          {listing?.propertyType && (
                            <Badge variant="outline" className="text-[10px] h-5">
                              {String(listing.propertyType)}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Location */}
                      {getLocationString(listing?.location) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                          <MapPin className="w-3 h-3" />
                          {getLocationString(listing.location)}
                        </p>
                      )}

                      {/* Property details row */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground text-sm">{formatPrice(Number(listing?.listPrice ?? listing?.price ?? 0))}</span>
                        {(listing?.bedrooms || getListingFeature(listing, 'Bedroom') || getListingFeature(listing, 'Bedrooms')) ? (
                          <span className="flex items-center gap-1">
                            <Bed className="w-3 h-3" /> {Number(listing?.bedrooms || getListingFeature(listing, 'Bedroom') || getListingFeature(listing, 'Bedrooms'))}
                          </span>
                        ) : null}
                        {(listing?.bathrooms || getListingFeature(listing, 'Bathroom') || getListingFeature(listing, 'Bathrooms')) ? (
                          <span className="flex items-center gap-1">
                            <Bath className="w-3 h-3" /> {Number(listing?.bathrooms || getListingFeature(listing, 'Bathroom') || getListingFeature(listing, 'Bathrooms'))}
                          </span>
                        ) : null}
                        {typeof listing?.area === 'number' && listing.area > 0 && (
                          <span className="flex items-center gap-1">
                            <Ruler className="w-3 h-3" /> {listing.area}m&sup2;
                          </span>
                        )}
                        {Array.isArray(listing?.images) && listing.images.length > 0 && (
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" /> {listing.images.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-Import Settings Dialog */}
      <Dialog open={showAutoImportSettings} onOpenChange={setShowAutoImportSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auto-Import Settings</DialogTitle>
            <DialogDescription>
              Configure automatic property synchronization from external API
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-import-enabled">Enable Auto-Import</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically sync properties at regular intervals
                </p>
              </div>
              <Switch
                id="auto-import-enabled"
                checked={autoImportSettings.enabled}
                onCheckedChange={(checked) =>
                  setAutoImportSettings({ ...autoImportSettings, enabled: checked })
                }
              />
            </div>

            <Separator />

            {/* Interval Selection */}
            <div className="space-y-2">
              <Label htmlFor="import-interval">Sync Interval</Label>
              <Select
                value={String(autoImportSettings.interval)}
                onValueChange={(value) =>
                  setAutoImportSettings({ ...autoImportSettings, interval: parseInt(value) })
                }
                disabled={!autoImportSettings.enabled}
              >
                <SelectTrigger id="import-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Every 5 minutes</SelectItem>
                  <SelectItem value="10">Every 10 minutes</SelectItem>
                  <SelectItem value="15">Every 15 minutes</SelectItem>
                  <SelectItem value="30">Every 30 minutes</SelectItem>
                  <SelectItem value="60">Every hour</SelectItem>
                  <SelectItem value="120">Every 2 hours</SelectItem>
                  <SelectItem value="360">Every 6 hours</SelectItem>
                  <SelectItem value="720">Every 12 hours</SelectItem>
                  <SelectItem value="1440">Every 24 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Properties will be automatically synced from the external API at this interval
              </p>
            </div>

            {/* Status Info */}
            {autoImportSettings.enabled && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Auto-import is active. Properties will sync every {autoImportSettings.interval} minutes.
                  {lastSyncTime && (
                    <span className="block mt-1">
                      Last sync: {new Date(lastSyncTime).toLocaleString()}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              data-testid="property-listing-settings-cancel-button"
              variant="outline"
              onClick={() => setShowAutoImportSettings(false)}
              disabled={isSavingSettings}
            >
              Cancel
            </Button>
            <Button
              data-testid="property-listing-settings-save-button"
              onClick={saveAutoImportSettings}
              disabled={isSavingSettings}
              className="bg-[#FDB022] hover:bg-[#FDB022]/90 text-black"
            >
              {isSavingSettings ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Listing Preview Dialog */}
      <Dialog open={!!previewListing} onOpenChange={(open) => !open && setPreviewListing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewListing?.title || 'Listing Details'}</DialogTitle>
            <DialogDescription>
              External listing from {previewListing?.source || 'unknown source'}
            </DialogDescription>
          </DialogHeader>

          {previewListing && (
            <div className="space-y-4">
              {/* Images */}
              {previewListing.images && previewListing.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 rounded-lg overflow-hidden">
                  {previewListing.images.slice(0, 6).map((img, i) => {
                    const imgUrl = typeof img === 'string' ? img : (img as any)?.url || '';
                    return (
                      <img 
                        key={i} 
                        src={imgUrl} 
                        alt={`${previewListing.marketingHeading || previewListing.title} ${i + 1}`}
                        className={`w-full object-cover rounded-lg ${i === 0 ? 'col-span-2 row-span-2 h-48' : 'h-[92px]'}`}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-[#FDB022]">{formatPrice(previewListing.listPrice ?? previewListing.price)}</span>
                {previewListing.propertyType && (
                  <Badge variant="outline">{previewListing.propertyType}</Badge>
                )}
              </div>

              <Separator />

              {/* Location */}
              {previewListing.location && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Location</h4>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {getLocationString(previewListing.location)}
                  </p>
                </div>
              )}

              {/* Property Details */}
              <div>
                <h4 className="text-sm font-medium mb-2">Property Details</h4>
                <div className="grid grid-cols-3 gap-3">
                  {previewListing.propertyType && (
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Home className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Type</p>
                      <p className="text-sm font-medium">{previewListing.propertyType}</p>
                    </div>
                  )}
                  {previewListing.bedrooms != null && previewListing.bedrooms > 0 && (
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Bed className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Bedrooms</p>
                      <p className="text-sm font-medium">{previewListing.bedrooms}</p>
                    </div>
                  )}
                  {previewListing.bathrooms != null && previewListing.bathrooms > 0 && (
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Bath className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Bathrooms</p>
                      <p className="text-sm font-medium">{previewListing.bathrooms}</p>
                    </div>
                  )}
                  {previewListing.area != null && previewListing.area > 0 && (
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Ruler className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Area</p>
                      <p className="text-sm font-medium">{previewListing.area}m&sup2;</p>
                    </div>
                  )}
                  {previewListing.images && previewListing.images.length > 0 && (
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <ImageIcon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Photos</p>
                      <p className="text-sm font-medium">{previewListing.images.length}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {previewListing.description && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">
                    {previewListing.description}
                  </p>
                </div>
              )}

              <Separator />

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                {previewListing.somethingPropertyId && (
                  <div>
                    <span className="font-medium text-foreground">SP ID:</span>{' '}
                    <span className="font-mono">{previewListing.somethingPropertyId}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-foreground">Listing ID:</span>{' '}
                  <span className="font-mono">{previewListing.listingId}</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Source:</span>{' '}
                  {previewListing.source}
                </div>
                {previewListing.listingStatus && (
                  <div>
                    <span className="font-medium text-foreground">Status:</span>{' '}
                    <Badge variant="outline" className="text-[10px] h-5 ml-1">
                      {previewListing.listingStatus}
                    </Badge>
                  </div>
                )}
                <div>
                  <span className="font-medium text-foreground">Created:</span>{' '}
                  {formatTimestampFull(previewListing.createdAt)}
                </div>
                <div>
                  <span className="font-medium text-foreground">Last Synced:</span>{' '}
                  {formatTimestampFull(previewListing.lastSynced)}
                </div>
                {previewListing.url && (
                  <div className="col-span-2">
                    <span className="font-medium text-foreground">URL:</span>{' '}
                    <a 
                      href={previewListing.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#FDB022] hover:underline inline-flex items-center gap-1"
                    >
                      View on {previewListing.source || 'portal'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>

              {/* Agents from listing */}
              {previewListing.agents && previewListing.agents.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-2">Linked Agents ({previewListing.agents.length})</h4>
                    <div className="space-y-2">
                      {previewListing.agents.map((agent, i) => (
                        <div key={agent.agentId || i} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                          {agent.profilePicture ? (
                            <img
                              src={agent.profilePicture}
                              alt={`${agent.firstName} ${agent.lastName}`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#FDB022]/20 flex items-center justify-center text-xs font-bold text-[#FDB022]">
                              {agent.firstName?.[0]}{agent.lastName?.[0]}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {agent.firstName} {agent.lastName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {agent.email || agent.emailAddress || ''}
                              {agent.jobTitle ? ` - ${agent.jobTitle}` : ''}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0">
                            {agent.agentId}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}